/**
 * Saving one open document: two seconds after the last change it is sent with the page it was
 * read as (`base`), so the core merges it with what someone else saved since; what comes back
 * is shown when nothing was typed meanwhile. Someone else's save is read and shown when this
 * page has nothing unsaved, else this page saves at once (the core merges both).
 */
import { createSignal, type Accessor } from "solid-js";
import type { Page } from "@protocol/Page";
import { samePage, type Documents } from "../../../state/documents";
import { SAVE_AFTER_MS } from "./constants";

/** Where the open document stands. */
export type SaveStatus = "saved" | "unsaved" | "saving" | "refused";

/** What the saver is built from. */
export interface SaverDeps {
  /** The page's path. */
  path: string;
  documents: Pick<Documents, "save" | "read" | "onChanged">;
  /** This page's person (its own saves are not read back). */
  you: () => string | null;
  /** The page as the editor holds it now. */
  current: () => Page;
  /** Shows `page` in the editor (someone else's save, or the merge of both). */
  apply: (page: Page) => void;
  /** The blocks a merge found changed by both (theirs follows each). */
  conflicts: (ids: string[]) => void;
  /** Whether this person may save (editors). */
  canSave: () => boolean;
}

/** One open document's saver. */
export interface Saver {
  /** Where it stands. */
  status: Accessor<SaveStatus>;
  /** Starts from `page`, as read. */
  start(page: Page): void;
  /** Something changed: saved after {@link SAVE_AFTER_MS}. */
  touched(): void;
  /** Saves now, if anything changed. */
  flush(): Promise<void>;
  /** Saves what is left and stops listening. */
  stop(): void;
}

/** Starts the saver of one document. */
export function createSaver({ path, documents, you, current, apply, conflicts, canSave }: SaverDeps): Saver {
  const [status, setStatus] = createSignal<SaveStatus>("saved");
  let base: Page | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let saving: Promise<void> | null = null;
  let again = false;
  let stopped = false;

  /** Whether the editor holds something the core has not been sent. */
  const dirty = () => !!base && !samePage(current(), base);

  const flush = async (): Promise<void> => {
    clearTimeout(timer);
    timer = undefined;
    if (saving) {
      again = true;
      return saving;
    }
    if (!base || !canSave() || !dirty()) {
      if (!saving) setStatus("saved");
      return;
    }
    saving = (async () => {
      do {
        again = false;
        const sent = current();
        setStatus("saving");
        const result = await documents.save(path, base, sent);
        if (!result) {
          setStatus("refused");
          break;
        }
        if (!stopped && samePage(current(), sent)) {
          base = result.page;
          if (!samePage(result.page, sent)) apply(result.page);
        } else base = sent;
        conflicts(result.conflicts);
        setStatus(dirty() ? "unsaved" : "saved");
      } while (again && dirty());
      saving = null;
    })();
    return saving;
  };

  const forget = documents.onChanged((changed, by) => {
    if (changed !== path || stopped || (by && by === you())) return;
    if (dirty()) return void flush();
    void documents.read(path).then((page) => {
      if (!page || stopped || dirty() || saving) return;
      if (!base || !samePage(page, base)) {
        base = page;
        apply(page);
      }
    });
  });

  return {
    status,
    start(page) {
      base = page;
      setStatus("saved");
    },
    touched() {
      if (!canSave() || !base) return;
      setStatus(dirty() ? "unsaved" : "saved");
      clearTimeout(timer);
      timer = setTimeout(() => void flush(), SAVE_AFTER_MS);
    },
    flush,
    stop() {
      if (dirty()) void flush();
      stopped = true;
      clearTimeout(timer);
      forget();
    },
  };
}
